import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  CloseButton,
  Divider,
  Drawer,
  Flex,
  Group,
  LoadingOverlay,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { ChecksIcon, FunnelXIcon, ImageIcon, PlusIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { GoodsApi, PricingApi } from "../../apis"
import { currencyFormat } from "../../utils"
import { ColumnType, Table } from "../table"
import { CategoryTreeSelect, getDescendantIds } from "./category-tree-select"

export type Line = {
  variantId: string
  warehouseId: string
  quantity: number
  price: number
  discountType?: string
  discountValue?: number
}

interface Props {
  opened: boolean
  onClose: () => void
  businessId: string
  sellerBusinessId: string
  buyerBusinessId?: string | null
  orderType: "SALES" | "PURCHASE"
  selectedKeys: string[]
  onAdd: (line: Line) => void
  onAddAll?: (lines: Line[]) => void
  subtitle?: string
}

type IFilter = {
  query: string
  categoryId: string | null
  brandId: string | null
}

const EMPTY_FILTERS: IFilter = {
  query: "",
  categoryId: null,
  brandId: null,
}

type PriceRow = { finalPrice: number; b2bPrice: number | null; b2cPrice: number | null }

export function VariantSelectorDrawer({
  opened,
  onClose,
  businessId,
  sellerBusinessId,
  buyerBusinessId,
  orderType,
  selectedKeys,
  onAdd,
  onAddAll,
  subtitle,
}: Props) {
  const { t } = useTranslation(["order", "common"])
  const [filters, setFilters] = useState<IFilter>({ ...EMPTY_FILTERS })
  const refTable = useRef<any>(null)
  const [tableRows, setTableRows] = useState<any[]>([])
  const [rowWarehouseByVariant, setRowWarehouseByVariant] = useState<Record<string, string>>({})
  const [addingAll, setAddingAll] = useState(false)

  const { data: categoriesRaw } = useSWR(
    opened ? "swr.goods.categories" : null,
    () => GoodsApi.categories(),
    { revalidateOnFocus: false }
  )
  const { data: brandsRaw } = useSWR(
    opened ? "swr.goods.brands" : null,
    () => GoodsApi.brands(),
    { revalidateOnFocus: false }
  )

  const brandData = (brandsRaw || []).map((b) => ({ value: b.id, label: b.name }))

  const categories = useMemo(() => {
    if (!filters.categoryId || !categoriesRaw) return undefined
    return getDescendantIds(categoriesRaw, filters.categoryId)
  }, [filters.categoryId, categoriesRaw])

  const apiFilters = useMemo(() => {
    const { categoryId, ...rest } = filters
    return categories ? { ...rest, categories } : rest
  }, [filters, categories])

  const hasFilters = filters.categoryId !== null || filters.brandId !== null

  const visibleVariantIds = useMemo(
    () => tableRows.map((r: any) => r.id).filter(Boolean),
    [tableRows]
  )

  const { data: priceBreakdowns } = useSWR(
    opened && sellerBusinessId && visibleVariantIds.length > 0
      ? `swr.pricing.batch.${sellerBusinessId}.${buyerBusinessId ?? "null"}.${visibleVariantIds.join(",")}`
      : null,
    () =>
      PricingApi.calculateBatch({
        businessId: sellerBusinessId,
        buyerBusinessId: buyerBusinessId ?? null,
        variants: visibleVariantIds.map((id: string) => ({ variantId: id, quantity: 1 })),
      }),
    { revalidateOnFocus: false, keepPreviousData: true }
  )

  const priceByVariant = useMemo(() => {
    const map: Record<string, PriceRow> = {}
    for (const pb of (priceBreakdowns || []) as any[]) {
      map[pb.variantId] = {
        finalPrice: pb.finalPrice,
        b2bPrice: pb.basePrice?.b2bPrice ?? null,
        b2cPrice: pb.basePrice?.b2cPrice ?? pb.b2cPrice ?? null,
      }
    }
    return map
  }, [priceBreakdowns])

  const handleAddAll = async () => {
    try {
      setAddingAll(true)
      const lines: Line[] = []
      for (const r of tableRows) {
        const auto = r.inventories?.length === 1 ? r.inventories[0].warehouseId : ""
        const warehouseId = rowWarehouseByVariant[r.id] ?? auto
        const priceRow = priceByVariant[r.id]
        const price = priceRow?.finalPrice
        if (!warehouseId || price == null) continue
        if (selectedKeys.includes(`${r.id}-${warehouseId}`)) continue
        lines.push({ ...r, variantId: r.id, warehouseId, quantity: 1, price })
      }
      if (onAddAll && lines.length > 0) await onAddAll(lines)
    } finally {
      setAddingAll(false)
    }
  }

  const columns: ColumnType<any>[] = [
    {
      title: "#",
      width: "36px",
      render: (_r, index) => <Text size="sm" c="dimmed" fw={500}>{index + 1}</Text>,
    },
    {
      // Product name + brand + SKU
      title: t("variantSelector.colProduct"),
      sorter: true,
      dataIndex: "name",
      render: (record) => {
        const compoundKeys = (record.inventories || []).map(
          (w: any) => `${record.id}-${w.warehouseId}`
        )
        const isSelected = compoundKeys.some((k: string) => selectedKeys.includes(k)) ||
          selectedKeys.includes(record.id)
        const variantLabel = record?.optionValueNames?.length > 0
          ? record.optionValueNames.join(", ")
          : null
        return (
          <Group gap="sm" wrap="nowrap" style={{ opacity: isSelected ? 0.5 : 1 }}>
            <Avatar
              src={record.image ?? undefined}
              radius="sm"
              size={40}
              style={{ border: "1px solid var(--mantine-color-gray-2)", flexShrink: 0 }}
            >
              <ImageIcon size={14} />
            </Avatar>
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Text
                size="sm"
                fw={500}
                lh={1.3}
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {record?.nameMon ?? record?.name ?? "-"}
                {variantLabel && (
                  <Text span size="sm" c="dimmed" fw={400}> — {variantLabel}</Text>
                )}
              </Text>
              <Group gap={6} mt={2}>
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {record.skuCode ?? "-"}
                </Text>
                {record.barCode && (
                  <>
                    <Text size="sm" c="dimmed">·</Text>
                    <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {record.barCode}
                    </Text>
                  </>
                )}
                {isSelected && (
                  <Badge size="sm" variant="light" color="gray" ml={4}>
                    {t("variantSelector.added")}
                  </Badge>
                )}
              </Group>
            </Box>
          </Group>
        )
      },
    },
    {
      title: t("variantSelector.colWarehouse"),
      width: "260px",
      render: (record) => {
        const options = (record.inventories || []).map((inv: any) => ({
          value: inv.warehouseId,
          label: inv.warehouseName,
        }))
        const auto = options.length === 1 ? options[0].value : null
        const selected = rowWarehouseByVariant[record.id] ?? auto ?? null
        return (
          <Box w={240}>
            <Select
              size="sm"
              value={selected}
              onChange={(v) =>
                setRowWarehouseByVariant((s) => ({ ...s, [record.id]: v ?? "" }))
              }
              data={options}
              placeholder={t("variantSelector.selectPlaceholder")}
              disabled={options.length === 0}
              clearable={false}
            />
          </Box>
        )
      },
    },
    {
      title: "B2B",
      dataIndex: "id",
      titleAlign: "right",
      render: (record: any) => {
        const row = priceByVariant[record.id]
        if (!row || row.b2bPrice == null) return <Text size="sm" c="dimmed" ta="right">—</Text>
        return (
          <Text size="sm" fw={600} ta="right" style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
            {currencyFormat(row.b2bPrice)}
          </Text>
        )
      },
    },
    {
      title: "B2C",
      dataIndex: "id",
      titleAlign: "right",
      render: (record: any) => {
        const row = priceByVariant[record.id]
        if (!row || row.b2cPrice == null) return <Text size="sm" c="dimmed" ta="right">—</Text>
        return (
          <Text size="sm" fw={600} ta="right" style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
            {currencyFormat(row.b2cPrice)}
          </Text>
        )
      },
    },
    {
      title: t("variantSelector.colPrice"),
      dataIndex: "id",
      titleAlign: "right",
      render: (record: any) => {
        const row = priceByVariant[record.id]
        if (!row) return <Text size="sm" c="dimmed" ta="right">—</Text>
        return (
          <Text size="sm" fw={700} ta="right" style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
            {currencyFormat(row.finalPrice)}
          </Text>
        )
      },
    },
    {
      title: "",
      width: "44px",
      render: (record: any) => {
        const auto = record.inventories?.length === 1 ? record.inventories[0].warehouseId : ""
        const warehouseId = rowWarehouseByVariant[record.id] ?? auto
        const priceRow = priceByVariant[record.id]
        const price = priceRow?.finalPrice
        const compoundKey = `${record.id}-${warehouseId}`
        const alreadyAdded = selectedKeys.includes(compoundKey)
        const canAdd = !!warehouseId && price != null && !alreadyAdded
        return (
          <Tooltip
            label={alreadyAdded ? t("variantSelector.added") : t("variantSelector.add")}
            withArrow
            position="left"
          >
            <ActionIcon
              size="md"
              variant={alreadyAdded ? "filled" : "default"}
              color={alreadyAdded ? "dark" : "gray"}
              disabled={!canAdd}
              onClick={() => {
                if (canAdd) {
                  onAdd({ ...record, variantId: record.id, warehouseId, quantity: 1, price })
                }
              }}
            >
              <PlusIcon size={14} />
            </ActionIcon>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Drawer
      position="right"
      size="80rem"
      opened={opened}
      onClose={addingAll ? () => {} : onClose}
      closeOnClickOutside={!addingAll}
      closeOnEscape={!addingAll}
      title={false}
      withCloseButton={false}
      padding={0}
      styles={{ body: { height: "100%", display: "flex", flexDirection: "column" } }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <LoadingOverlay visible={addingAll} zIndex={200} overlayProps={{ blur: 2 }} />

        {/* Header */}
        <Box px="lg" pt="lg" pb="sm" style={{ flexShrink: 0 }}>
          <Flex justify="space-between" align="center" mb="md">
            <Box>
              <Text fw={700} fz="var(--nw-fz-card)" lh={1.3}>{t("variantSelector.title")}</Text>
              <Text size="sm" c="dimmed">
                {subtitle || t("variantSelector.subtitle")}
              </Text>
            </Box>
            <Group gap="sm">
              {selectedKeys.length > 0 && (
                <Badge size="lg" variant="light" color="gray">
                  {t("variantSelector.selectedCount", { count: selectedKeys.length })}
                </Badge>
              )}
              <Button
                variant="default"
                leftSection={<ChecksIcon size={16} />}
                onClick={handleAddAll}
                loading={addingAll}
              >
                {t("variantSelector.addAll")}
              </Button>
              <CloseButton size="lg" onClick={onClose} disabled={addingAll} />
            </Group>
          </Flex>

          {/* Search */}
          <TextInput
            placeholder={t("variantSelector.searchPlaceholder")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            size="sm"
            mb="sm"
            value={filters.query}
            onChange={(e) => setFilters({ ...filters, query: e.currentTarget.value })}
            rightSection={
              filters.query ? (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  c="dimmed"
                  onClick={() => setFilters({ ...filters, query: "" })}
                >
                  <XIcon size={14} />
                </ActionIcon>
              ) : null
            }
          />

          {/* Filters */}
          <Flex gap="sm" align="flex-end" wrap="wrap">
            <CategoryTreeSelect
              label={t("variantSelector.category")}
              data={categoriesRaw || []}
              value={filters.categoryId}
              onChange={(categoryId) => setFilters({ ...filters, categoryId })}
            />
            <Select
              label={t("variantSelector.brand")}
              placeholder={t("common:filter.all")}
              data={brandData}
              value={filters.brandId}
              onChange={(brandId) => setFilters({ ...filters, brandId })}
              searchable
              clearable
              size="sm"
              miw={200}
            />
            {hasFilters && (
              <Tooltip label={t("common:actions.clear")} withArrow>
                <ActionIcon
                  variant="default"
                  size="input-sm"
                  onClick={() =>
                    setFilters({ ...EMPTY_FILTERS, query: filters.query })
                  }
                >
                  <FunnelXIcon size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Flex>

          <Divider mt="md" />
        </Box>

        {/* Table */}
        <Box px="lg" pb="lg" style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          <Table
            ref={refTable}
            name="variant-selector.list"
            columns={columns}
            filters={apiFilters}
            loadData={(data: any) => GoodsApi.list({ ...data, filter: { ...(data?.filter ?? {}), orderType }, businessId })}
            onResult={(res: any) => {
              setTableRows(res?.rows || [])
            }}
          />
        </Box>
      </Box>
    </Drawer>
  )
}
